/* eslint-disable max-lines-per-function */

import React from "react";
import moment from "moment";
import { Link } from "@reach/router";
import classNames from "classnames";
import { Badge, TableCell, TableRow, withStyles } from "@material-ui/core";
import { PropertyDuplicates_interestList_property_duplicates_candidates as Candidate } from "types/gql-types";
import styles from "./table.module.css";

const StyledBadge = withStyles(() => ({
  root: { width: 40 },
  badge: {
    right: 8,
    padding: "2px 4px",
    color: "white",
  },
}))(Badge);

const highlightClassName = (fieldName: string, fields: string[]):string =>
  (fields.includes(fieldName) ? styles.highlight : "");

const formatAddress = (c: Candidate):string => {
  const useMailing = c.mailingAddress1 && c.mailingCity && c.mailingState && c.mailingZip;
  const address1 = useMailing ? c.mailingAddress1 : c.residenceAddress1;
  const address2 = useMailing ? c.mailingAddress2 : c.residenceAddress2;
  const state = useMailing ? c.mailingState : c.residenceState;
  const city = useMailing ? c.mailingCity : c.residenceCity;
  const zip = useMailing ? c.mailingZip : c.residenceZip;

  if (!address1 && !city && !state && !zip) {
    return "N/A";
  }
  return `${address1 || ""} ${address2 || ""} ${city || ""} ${state || ""} ${zip || ""}`;
};

interface BaseCandidateRowType {
  candidate: Candidate;
  matchedFields: string[];
  showSocial: boolean;
}

interface CandidateRowType {
  slideoutEnabled: boolean;
  checkedIds: string[];
  setCheckedIds: (ids:string[]) => void;
}

interface ModalCandidateRowType {
  earliest: boolean;
  selectedMergeId: string | null;
  setSelectedMergeId: (id:string) => void;
}

const BaseCandidateRow: React.FC<
{modal?: boolean}
& BaseCandidateRowType
& Partial<CandidateRowType>
& Partial<ModalCandidateRowType>
> = ({
  modal = false,
  candidate,
  slideoutEnabled = null,
  checkedIds = [],
  setCheckedIds = () => null,
  matchedFields,
  showSocial,
  earliest = false,
  selectedMergeId = null,
  setSelectedMergeId = () => null,
}) => {
  const profileUrl = slideoutEnabled ? (
    `interests/${candidate.id}/activity`
  ) : (
    `/dash/manager/interest/${candidate.id}/activity`
  );

  const nameFields = <>
    <span className={highlightClassName("first_name", matchedFields)}>
      {candidate.firstName || "N/A"}
    </span>
    &nbsp;
    <span className={highlightClassName("last_name", matchedFields)}>
      {candidate.lastName || "N/A"}
    </span>
  </>;

  return (
    <TableRow className={classNames({
      [styles.rowHighlight]: window.location.href.match(candidate.id),
    })}>
      <TableCell>
        <label style={{ padding: 3 }}>
          {modal ? (
            <input
              name="merge"
              type="radio"
              value={candidate.id}
              checked={candidate.id === selectedMergeId}
              onChange={() => setSelectedMergeId(candidate.id)}
            />
          ) : (
            <input
              type="checkbox"
              checked={checkedIds.includes(candidate.id)}
              onChange={(e) => {
                setCheckedIds(e.target.checked ? (
                  [...checkedIds, candidate.id]) : checkedIds.filter((id) => id !== candidate.id));
              }}
            />
          )}
        </label>
      </TableCell>
      <TableCell>
        <div className={styles.overflow}>
          {modal ? nameFields : (
            <Link to={profileUrl} >
              {nameFields}
            </Link>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className={styles.overflow}>{candidate.propertyName}</div>
      </TableCell>
      {modal && (
        <TableCell>
          <div className={styles.keepOverflow}>
            <div className={styles.overflow}>
              {moment(candidate.submittedAt * 1000).format("MM/DD/YY h:mm A")}
            </div>
            {earliest && <StyledBadge badgeContent="EARLIEST" color="primary" />}
          </div>
        </TableCell>
      )}
      {showSocial && (
        <TableCell>
          <div className={styles.overflow}>
            <span className={highlightClassName("ssn", matchedFields)}>
              {candidate.ssn || "N/A"}
            </span>
          </div>
        </TableCell>
      )}
      <TableCell>
        {candidate.userId === null ? (
          <div className={styles.overflow}>No User Found</div>
        ) : (<>
          <div className={styles.overflow}>{candidate.loginPhone || "No User Phone"}</div>
          <div className={styles.overflow}>{candidate.loginEmail || "No User Email"}</div>
        </>)}
      </TableCell>
      <TableCell>
        <div className={styles.overflow}>{candidate.phone || "No Applicant Phone"}</div>
        <div className={styles.overflow}>
          <span className={highlightClassName("email", matchedFields)}>
            {candidate.email || "No Applicant Email"}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <div className={styles.overflow}>{formatAddress(candidate)}</div>
      </TableCell>
      {!modal && (
        <TableCell>
          <div className={styles.overflow}>
            {moment(candidate.submittedAt * 1000).format("MM/DD/YY h:mm A")}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};

export const CandidateRow: React.FC<
BaseCandidateRowType
& CandidateRowType
> = (props) => <BaseCandidateRow {...props} />;

export const ModalCandidateRow: React.FC<
BaseCandidateRowType
& ModalCandidateRowType
> = (props) => <BaseCandidateRow modal {...props} />;
